import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  orderBy,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  getCountFromServer,
  setDoc,
  startAt,
  endAt,
  or,
  and,
  limit,
  startAfter,
  endBefore,
  limitToLast,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { Company } from "../model/Companies";
import { Groups } from "../model/Groups";
import { DeliverList, Messages, MessageTimestampToDate, Token, TokenTimestampToDate, Topic, TopicTimestampToDate } from "../model/Message";
import { ReplyButton } from "../model/ReplyButton";
import { ReadMessages, UserData } from "../model/Users";
import { Questionnaire } from "../model/Questionnaire";
import { EventParticipationHistory, EventParticipationHistorySearchParam } from "../model/EventParticipationHistory";
import companiesQuery from "./Companies.query";
import userQuery from "./User.query";

const messagesQuery = {
  // お知らせ新規作成
  addMessage: async (message: Messages, token?: Token[] | undefined, replyButtonSelected?: ReplyButton | undefined, questionnaireSelected?: Questionnaire | undefined) => {
    console.log("message addMessage access");
    try {
      message.isMunicipality = true; // 団体からのお知らせフラグ
      if(message.link === '' && message.url !== undefined && message.url !== ''){
        message.link = message.url;
      };
      // リンク
      delete message.url;
      delete message.fileUpload;
      if(message.fileupload == undefined){
        message.fileupload = '';
      }
      if(message.questionnaireFlg == undefined){
        message.questionnaireFlg = false;
      }else{
        message.questionnaireSendFlg = false;
      }
      if(message.pointSettingFlg == undefined || message.pointSettingFlg == false) {
        if (message.pointSettingFlg == undefined) delete message.pointSettingFlg;
        delete message.pointId;
        delete message.pointTypeId;
        delete message.pointTypeName;
        delete message.pointName;
        delete message.pointAssignmentPoint;
        delete message.pointCategoryId;
        delete message.pointCategoryName;
        delete message.pointAwardedFlg;
      }
      // 配信先グループ
      const topicList: string[] = [];
      if(message.topic !== undefined && message.topic.length > 0) {
        topicList.push(...message.topic);
      }
      // topicを除外
      delete message.topic;
      // topic,tokenの件数を設定
      if(message.select === 'topic') {
        message.topicLength = topicList.length;
        delete message.tokenLength;
      } else if(message.select === 'token') {
        delete message.topicLength;
        message.tokenLength = token?.length ?? 0;
      } else {
        delete message.topicLength;
        delete message.tokenLength;
      }
      const docRef = await addDoc(collection(db, "messages"), message);
      console.log("Message Document written with ID: ", docRef.id);
      await updateDoc(docRef,{id:docRef.id});
      if(token !== undefined){
        await messagesQuery.setToken(docRef.id, token, message.eventFlg, message.eventAt);
      }
      if(topicList.length > 0){
        await messagesQuery.setTopic(docRef.id, topicList, message);
      }
      if(replyButtonSelected !== undefined && replyButtonSelected.id !== 'undefined') {
        replyButtonSelected.messageId = docRef.id;
        await messagesQuery.setReplyButton(docRef.id, message, replyButtonSelected);
      }
      if(questionnaireSelected !== undefined && message.questionnaireFlg) {
        questionnaireSelected.messageId = docRef.id;
        await messagesQuery.setQuestionnaire(docRef.id, message, message.tenantId, questionnaireSelected);
      }
      return docRef.id;
    } catch (err: any) {
      throw err;
    }
  },
  // お知らせ取得
  getMessage: async (messageId: string) => {
    console.log("message getMessage access");
    const messageRef = await getDoc(doc(db, "messages", messageId));
    return MessageTimestampToDate(messageRef.data());
  },
  // お知らせ取得
  getTargetPageMessages: async (authorUid: string, tenantId: string, limitNum: number, targetDs: QueryDocumentSnapshot | null, pageGap: number) => {
    console.log("message getTargetPageMessages access");
    const queryData: any[] = [
      or(
        and(
          where('tenantId', '==', tenantId),
          where('isMunicipality', '==', true),
          where('draftFlg', '==', false)
        ),
        and(
          where('authorUid', '==', authorUid),
          where('draftFlg', '==', true)
        ),
        and(
          where('tenantId', '==', tenantId),
          where('isMunicipality', '==', false),
          where('draftFlg', '==', false),
          where('deliveredFlg', '==', true)
        )
      ),
      orderBy("sendDate", "desc")
    ];
    if (pageGap === 0) {
      // 先頭ページ
      queryData.push(limit(limitNum));
    } else {
      const limitPage = Math.abs(limitNum * pageGap);
      if (pageGap > 0) {
        // 次ページ
        queryData.push(startAfter(targetDs));
        queryData.push(limit(limitPage));
      } else {
        // 前ページ
        queryData.push(endBefore(targetDs));
        queryData.push(limitToLast(limitPage));
      }
    }
    const messageRef = await getDocs(
      query(
        collection(db, 'messages'),
        ...queryData
      )
    );

    let startIndex = 0;
    let endIndex = limitNum;
    const data = messageRef.docs.map((item) => MessageTimestampToDate(item.data()));

    if (pageGap > 0) {
      // 次ページ：後ろから limitNum 件取り出す
      startIndex = Math.max(data.length - limitNum, 0);
      endIndex = data.length;
    }

    const returnData = data.slice(startIndex, endIndex);
    const startDs = messageRef.docs.length > 0
      ? messageRef.docs.at(startIndex) ?? null
      : null;
    const endDs = messageRef.docs.length > 0
      ? messageRef.docs.at(Math.min(startIndex + limitNum - 1, messageRef.docs.length - 1)) ?? null
      : null;

    return {
      data: returnData,
      startDs: startDs,
      endDs: endDs
    };
  },
  // お知らせ件数取得
  getCountAllMessages: async (authorUid: string, tenantId: string) => {
    console.log("message getCountAllMessages access");
    console.log(new Date());
    const messageRef = await getCountFromServer(
      query(
        collection(db, 'messages'),
        or(
          and(
            where('tenantId', '==', tenantId),
            where('isMunicipality', '==', true),
            where('draftFlg', '==', false)
          ),
          and(
            where('authorUid', '==', authorUid),
            where('draftFlg', '==', true)
          ),
          and(
            where('tenantId', '==', tenantId),
            where('isMunicipality', '==', false),
            where('draftFlg', '==', false),
            where('deliveredFlg', '==', true)
          )
        ),
        orderBy("sendDate", "desc")
      )
    );
    return messageRef.data().count;
  },
  // 検索機能を追加する場合のお知らせ取得コード例
  // （動かす際はインデックスの追加が必要）
  gettestdata: async (authorUid: string, tenantId: string, limitNum: number) => {
    console.log("message gettestdata access");
    const startDate = new Date(2025, 1, 1);
    const endDate = new Date(2025, 5, 31);
    const messageRef = await getDocs(
      query(
        collection(db, 'messages'),
        and(
          or(
            and(
              where('tenantId', '==', tenantId),
              where('isMunicipality', '==', true),
              where('draftFlg', '==', false)
            ),
            and(
              where('authorUid', '==', authorUid),
              where('draftFlg', '==', true)
            ),
            and(
              where('tenantId', '==', tenantId),
              where('isMunicipality', '==', false),
              where('draftFlg', '==', false),
              where('deliveredFlg', '==', true)
            )
          ),
          where('title', '>=', 'お知らせテスト'),
          where('title', '<=', 'お知らせテスト\uf8ff'),
          where('sendDate', '>=', startDate),
          where('sendDate', '<=', endDate),
        ),
        orderBy('title'),
        orderBy("sendDate", "desc"),
        limit(limitNum)
      )
    );
    return messageRef.docs.map((item) => MessageTimestampToDate(item.data()));
  },
  // お知らせに付加された返信ボタン取得
  getReplyButton: async (messageId: string) => {
    console.log("message getReplyButton access");
    const replyButtonRef =  await getDocs(collection(db, `messages/${messageId}/replyButtons`));
    return replyButtonRef.docs.map((item) => item.data() as ReplyButton)[0];
  },
  getQuestionnaire: async (messageId: string) => {
    console.log("message getQuestionnaire access");
    const questionnaireRef =  await getDocs(collection(db, `messages/${messageId}/questionnaire`));
    return questionnaireRef.docs.map((item) => item.data() as Questionnaire)[0];
  },
  getMessagesReplyButton: async (tenantId: string) => {
    console.log("message getMessagesReplyButton access");
    const replyButtonRef = await getDocs(
      query(collectionGroup(db, `replyButtons`), where("tenantId", "==", tenantId)));
    return replyButtonRef.docs.map((item) => item.data() as ReplyButton);
  },
  getReadedAndReply: async (messageId: string) => {
    console.log("message getReadedAndReply access");
    const readedRef = await getDocs(
      query(collectionGroup(db, 'readMessages'),
        where('messageId', '==', messageId)));
    return readedRef.docs.map((item) => item.data() as ReadMessages);
  },
  // お知らせ更新
  updateMessage: async (message: Messages, token?: Token[] | undefined, replyButtonSelected?: ReplyButton | undefined, questionnaireSelected?: Questionnaire | undefined) => {
    console.log("message updateMessage access");
    try {
      const messageRef = doc(db, "messages", message.id);

      // 掲載終了日
      const publishEndDate = !Number.isNaN(message?.publishEndAt?.getTime()) && message?.publishEndAt?.getTime() !== undefined
        ? Timestamp.fromDate(message?.publishEndAt ?? new Date())
        : deleteField();

      // 配信先グループ
      const topicList: string[] = [];
      if(message.topic !== undefined && message.topic.length > 0) {
        topicList.push(...message.topic);
      }

      await updateDoc(messageRef, {
        title: message.title,
        body: message.body,
        urlButtonFlg: message.urlButtonFlg !== undefined ? message.urlButtonFlg : deleteField(),
        urlButtonName: message.urlButtonFlg !== undefined ? message.urlButtonName : deleteField(),
        urlLink: message.urlButtonFlg !== undefined ? message.urlLink : deleteField(),
        image: message.image,
        contactInformation: message.contactInformation,
        eventVenue: message.eventVenue,
        type: message.type,
        link: message.link,
        fileupload: message.fileupload,
        topicLength:message.select==='topic' ? topicList.length : deleteField(),
        tokenLength:message.select==='token' ? (token?.length ?? 0) : deleteField(),
        select:message.select,
        draftFlg: message.draftFlg,
        authorUid: message.authorUid,
        deliveredFlg: message.deliveredFlg,
        eventFlg: message.eventFlg,
        eventAt: Timestamp.fromDate(message.eventAt),
        eventEndAt: Timestamp.fromDate(message.eventEndAt),
        eventType: message.eventType,
        publishEndAt: publishEndDate,
        sendDate: Timestamp.fromDate(message.sendDate),
        createAt: Timestamp.fromDate(message.createAt),
        updateAt: serverTimestamp(),
        replyButton: message.replyButton,
        isEventTimeEmpty: message.isEventTimeEmpty,
        questionnaireFlg:message.questionnaireFlg !== undefined ? message.questionnaireFlg : deleteField(),
        questionnaireSendFlg:message.questionnaireFlg !== undefined ? false : deleteField(),
        pointId: message.pointId !== undefined ? message.pointId : deleteField(),
        pointSettingFlg: message.pointSettingFlg !== undefined ? message.pointSettingFlg : deleteField(),
        pointTypeId: message.pointTypeId !== undefined ? message.pointTypeId : deleteField(),
        pointTypeName: message.pointTypeName !== undefined ? message.pointTypeName : deleteField(),
        pointAssignmentPoint: message.pointAssignmentPoint !== undefined ? message.pointAssignmentPoint : deleteField(),
        pointName: message.pointName !== undefined ? message.pointName : deleteField(),
        pointCategoryId: message.pointCategoryId !== undefined ? message.pointCategoryId : deleteField(),
        pointCategoryName: message.pointCategoryName !== undefined ? message.pointCategoryName : deleteField(),
        pointAwardedFlg: message.pointAwardedFlg !== undefined ? message.pointAwardedFlg : deleteField()
      });

      // 前に登録されているTokenを一旦削除する
      const tokenRef = await getDocs(collection(db, `messages/${message.id}/token`));
      if(tokenRef !== undefined){
        await Promise.all(tokenRef.docs.map(async (document): Promise<void> => {
          await deleteDoc(doc(db, document.ref.path));
        }));
      }
      if(token !== undefined){
        await messagesQuery.setToken(message.id, token, message.eventFlg, message.eventAt);
      }
      // 登録されているTopicの更新
      const topicRef = await getDocs(collection(db, `messages/${message.id}/topic`));
      const targetDeleteTopicId: string[] = []
      if(topicRef !== undefined){
        const topicListSet = new Set(topicList);
        topicRef.docs.forEach(doc => {
          if (!topicListSet.has(doc.id)) {
            targetDeleteTopicId.push(doc.id);
          }
        });
        await messagesQuery.deleteTopic(message.id, targetDeleteTopicId);
      }
      if(topicList.length > 0){
        await messagesQuery.setTopic(message.id, topicList, message);
      }
      // 前に登録されているreplyButtonを一旦削除する
      const replyButtonRef = await getDocs(collection(db, `messages/${message.id}/replyButtons`));
      if(replyButtonRef !== undefined) {
        await Promise.all(replyButtonRef.docs.map(async (document): Promise<void> => {
          await deleteDoc(doc(db,document.ref.path));
        }));
      }
      if(replyButtonSelected !== undefined && replyButtonSelected.id !== "undefined") {
        replyButtonSelected.messageId = message.id;
        await messagesQuery.setReplyButton(message.id, message, replyButtonSelected);
      }
      // 前に登録されているquestionnaireを一旦削除する
      const questionnaireRef = await getDocs(collection(db, `messages/${message.id}/questionnaire`));
      if(questionnaireRef !== undefined) {
        await Promise.all(questionnaireRef.docs.map(async (document): Promise<void> => {
          await deleteDoc(doc(db,document.ref.path));
        }));
      }
      if(questionnaireSelected !== undefined && message.questionnaireFlg) {
        questionnaireSelected.messageId = message.id;
        await messagesQuery.setQuestionnaire(message.id, message, message.tenantId, questionnaireSelected);
      }
    } catch (err) {
      throw err;
    }
  },
  // お知らせ削除
  deleteMessage: async (id: string) => {
    console.log("message deleteMessage access");
    try {
      await deleteDoc(doc(db, "messages", id));
    } catch (err) {
      throw err;
    }
  },
  // Topic削除
  deleteTopic: async (messageId: string, topicId: string[] = []) => {
    console.log("message deleteTopic access");
    try {
      let batchCount = 0;

      if (topicId.length > 0) {
        // topicIdを渡された場合、それを削除
        for (let i = 0; i < topicId.length; i += 500) {
          const batch = writeBatch(db);
          const chunk = topicId.slice(i, i + 500);

          chunk.forEach((id) => {
            batch.delete(doc(db, `messages/${messageId}/topic`, id));
          });

          await batch.commit();
          batchCount++;
          console.log(`Batch ${batchCount} committed (${chunk.length} docs).`);
        }
      } else {
        // topicIdを渡されなかった場合、全て削除
        const querySnapshot = await getDocs(collection(db, `messages/${messageId}/topic`));
        const docs = querySnapshot.docs;

        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(db);
          const chunk = docs.slice(i, i + 500);

          chunk.forEach((document) => {
            batch.delete(doc(db, `messages/${messageId}/topic`, document.id));
          });

          await batch.commit();
          batchCount++;
          console.log(`Batch ${batchCount} committed (${chunk.length} docs).`);
        }
      }
    } catch (err) {
      console.error("Error deleting topic:", err);
      throw err;
    }
  },
  // 団体でToken取得
  getMunicipalitiesToken: async (tenantId: string) => {
    console.log("message getMunicipalitiesToken access");
    const tokenList = await getDocs(
      query(collectionGroup(db, "token"),
        where('tenantId', '==', tenantId)));
    return tokenList.docs.map((doc) => TokenTimestampToDate(doc.data()) as Token);
  },
  // お知らせtopic登録
  setTopic: async (messageId: string, topic: string[], message: Messages) => {
    console.log("message setTopic access");
    try {
      const now = new Date();
      const chunkSize = 500;

      for (let i = 0; i < topic.length; i += chunkSize) {
        const chunk = topic.slice(i, i + chunkSize);
        const batch = writeBatch(db);

        chunk.forEach((item) => {
          const topicRef = doc(db, `messages/${messageId}/topic/${item}`);
          const topicData: Topic = {
            id: item,
            tenantId: message.tenantId,
            messageId: messageId,
            draftFlg: message.draftFlg,
            deliveredFlg: message.deliveredFlg,
            sendDate: message.sendDate,
            eventFlg: message.eventFlg,
            eventAt: message.eventFlg ? message.eventAt : null,
            updateAt: now
          };
          batch.set(topicRef, topicData);
        });

        await batch.commit();
      }
    } catch (err) {
      console.error(err);
    }
  },
  // お知らせtoken登録
  setToken: async (messageId: string, token: Token[], eventFlg: boolean, eventAt: Date) => {
    console.log("message setToken access");
    try {
      token.forEach(async (item) => {
        item.messageId = messageId;
        item.eventFlg = eventFlg;
        item.eventAt = eventAt;
        await addDoc(collection(db, `messages/${messageId}/token`), item);
      });
    } catch (err) {
      console.error(err);
    }
  },
  // 返信ボタン登録
  setReplyButton: async (messageId: string, message: Messages, replyButton: ReplyButton) => {
    console.log("message setReplyButton access");
    try {
      // sendDate を追加
      replyButton.sendDate = message.sendDate;
      // eventAt を追加
      replyButton.eventAt = message.eventAt;

      await addDoc(collection(db, `messages/${messageId}/replyButtons`), replyButton);
    } catch (err) {
      console.error(err);
    }
  },
  // アンケート登録
  setQuestionnaire: async (messageId: string, message: Messages, tenantId: string, questionnaire: Questionnaire) => {
    console.log("message setQuestionnaire access");
    try {
      // answers1, answers2, answers3 それぞれをチェックして undefined を取り除く
      const cleanedQuestionnaire = Object.fromEntries(
        Object.entries(questionnaire).filter(([_, v]) => v !== undefined && v !== null && v !== "")
      );
  
      const cleanAnswers = (answers: any[]) => {
        return answers ? answers.filter(answer => answer !== undefined && answer !== null && answer !== "") : [];
      };
  
      cleanedQuestionnaire.answers1 = cleanAnswers(questionnaire.answers1);
      cleanedQuestionnaire.answers2 = cleanAnswers(questionnaire.answers2);
      cleanedQuestionnaire.answers3 = cleanAnswers(questionnaire.answers3);

      // tenantId を追加
      cleanedQuestionnaire.tenantId = tenantId;

      // sendDate を追加
      cleanedQuestionnaire.sendDate = message.sendDate;
      // eventAt を追加
      cleanedQuestionnaire.eventAt = message.eventAt;

      await addDoc(collection(db, `messages/${messageId}/questionnaire`), cleanedQuestionnaire);
    } catch (err) {
      console.error(err);
    }
  },
  // お知らせ一覧取得
  getMessagesList: async (authorUid: string | undefined, company: Company, groups: Groups[]) => {
    // 団体用
    console.log("message getMessagesList access");
    const messageList: Messages[] = [];
    // topic: allまたは該当団体番号(company.displayId)あてのものまたは該当団体に属するグループIDあてのもの
    // topicリストを作成
    const topicList = groups.map((group) => group.id);
    topicList.push(company.displayId);
    const list = await getDocs(
      query(collection(db, "messages"),
        where("tenantId", "==", company.id),
        where('draftFlg', '==', false)
      )
    );
    list.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
      const targetData = doc.data();

      // topic（配信先団体、グループ）を取得
      const targetTopic = targetData.topic;
      if (targetTopic.length > 0) {
        /* topicが0件以上ある（全体配信、グループ配信）場合 */
        // お知らせに登録されているtopicがtopicリストに存在するか
        const searchResult = targetTopic.filter((topic1: string) => {
          return topicList.find((topic2: string) => {
            return topic1 === topic2;
          })
        })

        // messageListへ追加
        if (searchResult.length > 0) messageList.push(MessageTimestampToDate(targetData));
      } else {
          /* topicが0件（個別配信）の場合 */
          // messageListへ追加
          messageList.push(MessageTimestampToDate(targetData));
      }
    });
    // 下書き抽出
    const docsRef = await getDocs(
      query(
        collection(db, 'messages'),
        where('authorUid', '==', authorUid),
        where('draftFlg', '==', true)
      )
    );
    docsRef.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
      messageList.push(MessageTimestampToDate(doc.data()));
    });
    // 重複を取り除いてリターン
    return Array.from(new Map(messageList.map((message) => [message.id, message])).values());
  },
  // 予定一覧取得
  getEventMessagesList: async (authorUid: string | undefined, company: Company, groups: Groups[], month: Date) => {
    // 団体用
    console.log("message getEventMessagesList access");
    const messageList: Messages[] = [];
    // topic: allまたは該当団体番号(company.displayId)あてのものまたは該当団体に属するグループIDあてのもの
    //        → array_contains_anyは10件までなので、配列を10件ごとに分けて検索
    // topicリストを作成
    const topicList = groups.map((group) => group.id);
    topicList.push("all", company.displayId);
    // topicリストを10件ごとに検索
    for(let i = 0; i < topicList.length / 10; i++){
      const copyList = topicList.slice(i * 10, i * 10 + 10);
      const list = await getDocs(
        query(collection(db, "messages"),
          where("topic", "array-contains-any", copyList),
          where("eventFlg", "==", true),
          where("eventAt", ">=", new Date(month.getFullYear(), month.getMonth(), 1, 0, 0)),
          where("eventAt", "<=", new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59)),
          orderBy("eventAt", "asc")
        )
      );
      list.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        if(!doc.data().draftFlg) messageList.push(MessageTimestampToDate(doc.data()));
        if(doc.data().draftFlg && doc.data().authorUid === authorUid) messageList.push(MessageTimestampToDate(doc.data()));
      });
    }
    // token: 該当団体に属するtenantId(company.id)を持つもの
    //        → collectionGroupでtokenにtenantIdを持つmessagesのdocumentIdリストを作成
    //        → documentIdのリストをmapしてmessageを取得
    // messagesのdocumentIdリスト作成
    const tokenDocsRef = await getDocs(
      query(collectionGroup(db, "token"),
        where("tenantId", "==", company.id)
      )
    );
    const messageDocumentIdList = tokenDocsRef.docs.map((doc) =>
      doc.ref.parent.parent?.id);
    // messageDocumentIdListを10件ごとに検索
    for(let i = 0; i < messageDocumentIdList.length / 10; i++){
      const copyList = messageDocumentIdList.slice(i * 10, i * 10 + 10);
      const list = await getDocs(
        query(collection(db, "messages"),
          where("id", "in", copyList),
          where("eventFlg", "==", true),
          where("eventAt", ">=", new Date(month.getFullYear(), month.getMonth(), 1, 0, 0)),
          where("eventAt", "<=", new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59)),
          orderBy("eventAt", "asc")
        )
      );
      list.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        if(!doc.data().draftFlg) messageList.push(MessageTimestampToDate(doc.data()));
        if(doc.data().draftFlg && doc.data().authorUid === authorUid) messageList.push(MessageTimestampToDate(doc.data()));
      });
    }
    // 下書き抽出
    const docsRef = await getDocs(
      query(
        collection(db, 'messages'),
        where("eventFlg", "==", true),
        where("eventAt", ">=", new Date(month.getFullYear(), month.getMonth(), 1, 0, 0)),
        where("eventAt", "<=", new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59)),
        where('authorUid', '==', authorUid),
        where('draftFlg', '==', true),
        orderBy("eventAt", "asc")
      )
    );
    docsRef.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
      messageList.push(MessageTimestampToDate(doc.data()));
    });
    // 重複を取り除いてリターン
    return Array.from(new Map(messageList.map((message) => [message.id, message])).values());
  },

  // 今月の投稿数の取得
  getMonthlyMessageCount: async (companyId: string): Promise<number> => {
    console.log("message getMonthlyMessageCount access");
    const nowDate = new Date();
    const dateFrom = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1, 0, 0);
    const dateTo = new Date(nowDate.getFullYear(), nowDate.getMonth() + 1);

    // 当月配信済の件数
    const refDelivered = await getCountFromServer(
      query(
        collection(db, 'messages'), 
        where('tenantId', '==', companyId),
        where('isMunicipality', '==', true),  // 団体の配信
        where('draftFlg', '==', false),       // 下書以外
        where('sendDate', '>=', dateFrom),    // 当月の配信
        where('sendDate', '<', dateTo),
        where('deliveredFlg', '==', true),    // 配信済
      )
    );

    // 当月配信し掲載終了済の件数
    const refPublishEnd = await getCountFromServer(
      query(
        collection(db, 'messages'), 
        where('tenantId', '==', companyId),
        where('isMunicipality', '==', true),  // 団体の配信
        where('draftFlg', '==', false),       // 下書以外
        where('sendDate', '>=', dateFrom),    // 当月の配信
        where('sendDate', '<', dateTo),
        where('deliveredFlg', '==', false),   // 未配信
        where('publishEndAt', '<', nowDate),  // 掲載終了済
      )
    );

    return refDelivered.data().count + refPublishEnd.data().count;
  },

  // お知らせに付加されたDeliverList取得
  getDeliverList: async (messageId: string) => {
    console.log("message getDeliverList access");
    const deliverList =  await getDocs(collection(db, `messages/${messageId}/deliverList`));
    return deliverList.docs.map((doc) => doc.data() as DeliverList);
  },
  // お知らせに付加されたTopic取得
  getTopic: async (messageId: string) => {
    console.log("message getTopic access");
    const topicList =  await getDocs(collection(db, `messages/${messageId}/topic`));
    return topicList.docs.map((doc) => TopicTimestampToDate(doc.data()) as Topic);
  },
  // お知らせに付加されたToken取得
  getToken: async (messageId: string) => {
    console.log("message getToken access");
    const tokenList =  await getDocs(collection(db, `messages/${messageId}/token`));
    return tokenList.docs.map((doc) => TokenTimestampToDate(doc.data()) as Token);
  },

  // イベントサポート側で個別配信したお知らせか
  getUserSearch: async (authorUid: String, companyId: String) => {
    console.log("user getUserSearch access");
    let usersRef;
    if (authorUid) {
      usersRef = await getDocs(query(
        collection(db, `companies/${companyId}/users`),
        where('id', '==', authorUid),
      ));
      if (usersRef.docs.map((doc) => doc.data()).length > 0) {
        return true;
      } else {
        return false;
      }
    } else {
      console.error("authorUid is undefined");
      return false;
    }
  },

  // イベントサポート側の確定状況取得
  getStaffConfirmed: async (messageId: string): Promise<boolean | undefined> => {
    console.log("message getStaffConfirmed access");
    try {
      const messageRef = doc(db, `messages/${messageId}`);
      const messageDoc = await getDoc(messageRef);
      const data = messageDoc.data();
      const staffConfirmedFlg = data?.staffConfirmedFlg;
      return staffConfirmedFlg;
    } catch (err) {
      console.error(err);
      return undefined;
    }
  },

  // 団体側の確定状況取得
  getMunicipalityConfirmed: async (messageId: string): Promise<boolean | undefined> => {
    console.log("message getMunicipalityConfirmed access");
    try {
      const messageRef = doc(db, `messages/${messageId}`);
      const messageDoc = await getDoc(messageRef);
      const data = messageDoc.data();
      const municipalityConfirmedFlg = data?.municipalityConfirmedFlg;
      return municipalityConfirmedFlg;
    } catch (err) {
      console.error(err);
      return undefined;
    }
  },

  // 編集確定・解除の設定
  setConfirmed: async (messageId: string, municipalityConfirmedFlg: boolean) => {
    console.log("message setConfirmed access");
    try {
      const messageRef = doc(db, `messages/${messageId}`);
      await setDoc(messageRef, { municipalityConfirmedFlg: municipalityConfirmedFlg }, { merge: true });
    } catch (err) {
      console.error(err);
    }
  },

  // ポイント付与フラグ更新
  setPointAwarded: async (messageId: string, pointAwardedFlg: boolean) => {
    console.log("message setPointAwarded access");
    try {
      const messageRef = doc(db, `messages/${messageId}`);
      await setDoc(messageRef, { pointAwardedFlg: pointAwardedFlg }, { merge: true });
    } catch (err) {
      console.error(err);
    }
  },

  // イベント参加履歴取得
  getEventParticipationHistory: async (
    tenantId: string,
    searchValue: EventParticipationHistorySearchParam
  ) => {
    console.log("message getEventParticipationHistory access");
    const queries: any[] = [
      where('tenantId', "==", tenantId),
      where('isMunicipalityConfirmed', "==", true),
    ];

    // お知らせID
    if (searchValue.messageId !== "") {
      queries.push(where('messageId', "==", searchValue.messageId));
    }

    // 利用者の回答
    const targetReplyList = searchValue.replyListValue.filter((reply) => reply.value === true).map((reply) => reply.id);
    const targetReplyListLength = targetReplyList.length;
    if (targetReplyListLength > 0) {
      queries.push(where('replyButtonName', "in", targetReplyList));
    }
    // 出欠結果
    const targetAttendanceList = searchValue.attendanceListValue.filter((attendance) => attendance.value === true).map((attendance) => attendance.id.replace('attendance-', ''));
    const targetAttendanceListLength = targetAttendanceList.length;
    if (targetAttendanceListLength > 0) {
      queries.push(where('attendanceButtonName', "in", targetAttendanceList));
    }
    // 配信先
    if (searchValue.publishTarget !== "") {
      queries.push(where('publishTarget', "==", searchValue.publishTarget));
    }
    // 利用者番号
    if (searchValue.userId !== "") {
      queries.push(where('userDisplayId', "==", searchValue.userId));
    }

    if (!searchValue.stringCombinationSearchFlg) {
      // MEMO: インデックスの大量作成を避けるため、検索条件が文字列検索の時のみ条件に追加する
      // お知らせ詳細から遷移した場合は含めない
      if (searchValue.messageId === ""){
        // 名前
        if (searchValue.lastName !== "") {
          queries.push(orderBy('lastName'), startAt(searchValue.lastName), endAt(searchValue.lastName + "\uf8ff"));
        }
        // タイトル
        if (searchValue.title !== "") {
          queries.push(orderBy('title'), startAt(searchValue.title), endAt(searchValue.title + "\uf8ff"));
        }
        // 会場
        if (searchValue.eventVenue !== "") {
          queries.push(orderBy('eventVenue'), startAt(searchValue.eventVenue), endAt(searchValue.eventVenue + "\uf8ff"));
        }
      }
    }

    if (!searchValue.timestampCombinationSearchFlg) {
      // MEMO: インデックスの大量作成を避けるため、検索条件が日付検索の時のみ条件に追加する
      // お知らせ詳細から遷移した場合は含めない
      if (searchValue.messageId === ""){
        // 配信日
        if (searchValue.sendDateStart !== "") {
          const startDate = new Date(searchValue.sendDateStart + "T00:00:00.000+09:00");
          queries.push(where('sendDate', ">=", startDate));
        }
        if (searchValue.sendDateEnd !== "") {
          const endDate = new Date(searchValue.sendDateEnd + "T23:59:59.999+09:00");
          queries.push(where('sendDate', "<=", endDate));
        }
        // イベント開催日
        if (searchValue.eventAtStart !== "") {
          const startDate = new Date(searchValue.eventAtStart + "T00:00:00.000+09:00");
          queries.push(where('eventAt', ">=", startDate));
        }
        if (searchValue.eventAtEnd !== "") {
          const endDate = new Date(searchValue.eventAtEnd + "T23:59:59.999+09:00");
          queries.push(where('eventAt', "<=", endDate));
        }
      }
    }

    // イベントタイプ
    if (searchValue.eventType !== "") {
      queries.push(where('eventType', "==", searchValue.eventType));
    }

    const targetGroup = searchValue.searchGroups.filter((group) => group.value === true).map((group) => group.id);
    const targetGroupLength = targetGroup.length;

    let histories: EventParticipationHistory[] = [];

    if (searchValue.publishTarget === "topic" && targetGroupLength > 0) {
      // 配信先グループを選択している場合
      // 現在の検索条件の分離数を求める
      const disjunctions = targetReplyListLength === 0 && targetAttendanceListLength === 0 ? 0 :
                            (targetReplyListLength === 0 ? 1 : targetReplyListLength) * (targetAttendanceListLength === 0 ? 1 : targetAttendanceListLength);
      // グループの分割数を求める
      const groupDisjunctions = disjunctions > 0 ? Math.floor(30 / disjunctions) : 30;
      // グループを分割
      const targetGroupList = targetGroup.reduce((preArr: any[][], current, index) => {
        if (index % groupDisjunctions) { return preArr } else { return [...preArr, ...[targetGroup.slice(index, index + groupDisjunctions)]] }
      },[] as any[][]);

      // 分割数分、繰り返し履歴を取得する
      const historiesList = await Promise.all(targetGroupList.map(async (groupList) => {
        const groupQueries = [...queries];
        groupQueries.push(where('publishTargetList', "array-contains-any", groupList));

        const snapshot = await getDocs(
          query(
            collectionGroup(db, "eventParticipationHistory"),
            ...groupQueries
          )
        );

        return snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => doc.data() as EventParticipationHistory);
      }));

      histories = historiesList.flat();
    } else {
      // 履歴を取得する
      const snapshot = await getDocs(
        query(
          collectionGroup(db, "eventParticipationHistory"),
          ...queries
        )
      );
      histories = snapshot.docs.map((doc) => doc.data() as EventParticipationHistory);
    }

    const existingMessageIds = new Set(histories.map(h => h.messageId));
    let oldHistories: EventParticipationHistory[] = [];
    // messageId、出欠結果が検索に設定されていない場合は旧データ取得
    if (searchValue.messageId === "" && targetAttendanceListLength == 0) {
      oldHistories = await messagesQuery.getOldEventParticipationHistory(tenantId, searchValue, existingMessageIds);
    }

    const allHistories = [...histories, ...oldHistories];

    return allHistories;
  },

  // 旧イベント履歴を取得する関数
  getOldEventParticipationHistory: async (
    tenantId: string,
    searchValue: EventParticipationHistorySearchParam,
    existingMessageIds: Set<string>
  ): Promise<EventParticipationHistory[]> => {

    const oldQueries: any[] = [
      where('replyButtonName', 'in', ['会場で参加', 'オンラインで参加', '欠席'])
    ];
    
    // 団体情報取得
    const currentUser = auth.currentUser;
    const company = await companiesQuery.getCompany(currentUser?.tenantId!);
    
    // 利用者番号
    if (searchValue.userId !== "") {
      // 利用者番号で絞り込み取得
      oldQueries.push(where('userId', "==", (company.displayId + searchValue.userId)));
    } else {
      // ログインしている団体のみ取得
      oldQueries.push(
        orderBy('userId'),
        startAt(company.displayId),
        endAt(company.displayId + '\uf8ff')
      );
    }
    // 旧イベント参加履歴取得（readMessagesから取得）
    const snapshot = await getDocs(
      query(collectionGroup(db, "readMessages"), ...oldQueries)
    );

    // attendanceButtonNameが存在しないかつmessageIdが重複しないデータのみ
    const filteredOld = snapshot.docs
      .map(doc => doc.data() as EventParticipationHistory)
      .filter(data =>
        data.attendanceButtonName === undefined &&
        !existingMessageIds.has(data.messageId)
    );

    const nowDate = new Date();

    const oldHistories = (await Promise.all(
      filteredOld.map(async (data) => {
        const msgDoc = await getDoc(doc(db, "messages", data.messageId));
        const msgData = msgDoc.exists() ? msgDoc.data() : null;
        // イベント終了時間が現在日時より過去
        const eventEndAt = msgData?.eventEndAt?.toDate?.();
        if (!eventEndAt || eventEndAt > nowDate) return null;
        const user = await userQuery.getUser(tenantId, data.userId) as UserData;
        const topicIds = (await messagesQuery.getTopic(data.messageId)).map((t) => t.id);
        return {
          ...data,
          title: msgData?.title ?? null,
          eventVenue: msgData?.eventVenue ?? null,
          sendDate: msgData?.sendDate  ?? null,
          eventAt: msgData?.eventAt ?? null,
          eventAtEnd: msgData?.eventAtEnd ?? null,
          eventType: msgData?.eventType ?? null,
          publishTarget: msgData?.select ?? null,
          publishTargetList: topicIds,
          lastName: user?.lastName ?? null,
          userDisplayId: user?.displayId ?? null,
          attendanceButtonName: "　", // 出欠結果表示しない
        };
      })
    )).filter(Boolean) as EventParticipationHistory[];

    // 検索条件
    const filteredOldHistories = oldHistories.filter(h => {
      // イベントタイプ
      if (searchValue.eventType && h.eventType !== searchValue.eventType) return false;
      
      // 配信日
      if (searchValue.sendDateStart) {
        const startDate = new Date(searchValue.sendDateStart + "T00:00:00.000+09:00");
        const sendDate = (h.sendDate instanceof Date ? h.sendDate : (h.sendDate as Timestamp)?.toDate?.());
        if (!sendDate || sendDate < startDate) return false;
      }
      if (searchValue.sendDateEnd) {
        const endDate = new Date(searchValue.sendDateEnd + "T23:59:59.999+09:00");
        const sendDate = (h.sendDate instanceof Date ? h.sendDate : (h.sendDate as Timestamp)?.toDate?.());
        if (!sendDate || sendDate > endDate) return false;
      }
      
      // イベント開催日
      if (searchValue.eventAtStart) {
        const startDate = new Date(searchValue.eventAtStart + "T00:00:00.000+09:00");
        const eventAt = (h.eventAt instanceof Date ? h.eventAt : (h.eventAt as Timestamp)?.toDate?.());
        if (!eventAt || eventAt < startDate) return false;
      }
      if (searchValue.eventAtEnd) {
        const endDate = new Date(searchValue.eventAtEnd + "T23:59:59.999+09:00");
        const eventAt = (h.eventAt instanceof Date ? h.eventAt : (h.eventAt as Timestamp)?.toDate?.());
        if (!eventAt || eventAt > endDate) return false;
      }
      
      // 会場
      if (searchValue.eventVenue && !h.eventVenue?.includes(searchValue.eventVenue)) return false;
      
      // タイトル
      if (searchValue.title && !h.title?.includes(searchValue.title)) return false;
      
      // 名前
      if (searchValue.lastName && !h.lastName?.startsWith(searchValue.lastName)) return false;
      
      // 利用者の回答
      const targetReplyList = searchValue.replyListValue
        .filter(reply => reply.value === true)
        .map(reply => reply.id);
      if (targetReplyList.length > 0 && !targetReplyList.includes(h.replyButtonName)) return false;
      
      // 配信先
      const selectedGroupIds = searchValue.searchGroups.filter(g => g.value).map(g => g.id);
      if (searchValue.publishTarget) {
        const isGroupSearch = searchValue.publishTarget === "topic" && selectedGroupIds.length > 0;
        const isGroupAllSearch = searchValue.publishTarget === "topic" && selectedGroupIds.length === 0;

        if (isGroupSearch) {
          // グループ配信（特定グループ）
          if (!h.publishTargetList?.some(id => selectedGroupIds.includes(id))) return false;

        } else if (isGroupAllSearch) {
          // 全グループ配信取得（特定グループは指定していない）
          // 全体配信を除く（company.displayIdが含まれてたら除外）
          if (h.publishTargetList?.includes(company.displayId)) return false;
          if (!h.publishTargetList || h.publishTargetList.length === 0) return false;

        } else if (searchValue.publishTarget === company.displayId) {
          // 全体配信取得
          if (!h.publishTargetList?.includes(company.displayId)) return false;

        } else {
          // 個別配信取得
          const isIndividualTarget =
            (h.publishTarget === searchValue.publishTarget) ||
            (
              !h.publishTarget &&
              (!h.publishTargetList || h.publishTargetList.length === 0)
            );

          if (!isIndividualTarget) return false;
        }
      }
      
      return true;
    });

    return filteredOldHistories;
  },

  // 出欠一覧 確定後のデータ取得
  getConfirmedDate: async (messageId: string) => {
    console.log("message getReadedAndReply access");
    const readedRef = await getDocs(
      query(collectionGroup(db, 'eventParticipationHistory'),
        where('messageId', '==', messageId)));
    return readedRef.docs.map((item) => item.data() as EventParticipationHistory);
  },
};

export default messagesQuery;
